import { useState, useEffect, useCallback } from 'react';
import { useToast } from './use-toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/auth';
import { 
  fetchFavorites,
  addFavorite,
  removeFavorite,
  migrateLocalStorageFavorites 
} from '@/services/favoriteService'; // Import the service functions

// Helper function to get favorites from localStorage
const getLocalFavorites = (): Set<string> => {
  try {
    const saved = localStorage.getItem('favorites');
    return saved ? new Set<string>(JSON.parse(saved)) : new Set<string>();
  } catch (error) {
    console.error("Error reading favorites from localStorage:", error);
    return new Set<string>();
  }
};

// Helper function to save favorites to localStorage
const saveLocalFavorites = (favoritesSet: Set<string>): void => {
  try {
    localStorage.setItem('favorites', JSON.stringify([...favoritesSet]));
  } catch (error) {
    console.error("Error saving favorites to localStorage:", error);
  }
};

interface UseFavoritesReturn {
  favorites: Set<string>;
  isLoading: boolean;
  toggleFavorite: (profileId: string) => Promise<void>;
  isFavorite: (profileId: string) => boolean; // Add helper function
}

export const useFavorites = (): UseFavoritesReturn => {
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Load favorites effect
  useEffect(() => {
    const loadFavorites = async () => {
      setIsLoading(true);
      let finalFavorites = new Set<string>();

      try {
        const localFavs = getLocalFavorites(); // Always load local first for immediate UI
        finalFavorites = localFavs; 
        
        if (user) {
          // User is logged in
          console.log('User logged in, syncing favorites...');
          try {
            const remoteFavsList = await fetchFavorites(user.id);
            const remoteFavs = new Set(remoteFavsList);
            
            // Combine local and remote, giving precedence to remote as source of truth
            // For simplicity here, we overwrite local state with remote state
            finalFavorites = remoteFavs;

            // Check if local storage had items not present remotely (potential migration needed)
            const localOnly = [...localFavs].filter(id => !remoteFavs.has(id));
            if (localOnly.length > 0) {
              console.log('Found favorites in localStorage not in DB, attempting migration...');
              const migrationSuccess = await migrateLocalStorageFavorites(user.id, localOnly);
              if (migrationSuccess) {
                // Add migrated items to the current state
                localOnly.forEach(id => finalFavorites.add(id));
                console.log('Migration successful, combined state:', finalFavorites);
              } else {
                 console.error('Migration failed. Some local favorites may not be saved remotely.');
                 // Optionally inform the user
              }
            }
          } catch (fetchError) {
            console.error('Failed to fetch/sync remote favorites, using localStorage data:', fetchError);
            finalFavorites = localFavs; // Fallback to local if fetch fails
            toast({
              title: 'Erreur de synchronisation',
              description: 'Impossible de charger les favoris depuis le serveur. Vérifiez votre connexion.',
              variant: 'default', // Changed from 'warning' to 'default'
            });
          }
        } else {
          // User not logged in, use only local storage
          console.log('User not logged in, using favorites from localStorage.');
          finalFavorites = localFavs;
        }
      } catch (error) {
         console.error('Unexpected error during favorite loading:', error);
         // Ensure local state is set even if errors occur
         finalFavorites = getLocalFavorites();
      } finally {
        setFavorites(finalFavorites);
        saveLocalFavorites(finalFavorites); // Ensure localStorage is synced with the final state
        setIsLoading(false);
        console.log('Favorites loading complete. Final state:', finalFavorites);
      }
    };

    loadFavorites();
  }, [user]); // Re-run when user logs in or out

  // Toggle favorite function
  const toggleFavorite = useCallback(async (profileId: string) => {
    const isCurrentlyFavorite = favorites.has(profileId);
    const optimisticFavorites = new Set(favorites);

    // Optimistic UI update
    if (isCurrentlyFavorite) {
      optimisticFavorites.delete(profileId);
    } else {
      optimisticFavorites.add(profileId);
    }
    setFavorites(optimisticFavorites);
    saveLocalFavorites(optimisticFavorites); // Update localStorage immediately

    // Perform backend operation if user is logged in
    if (user) {
      try {
        if (isCurrentlyFavorite) {
          await removeFavorite(user.id, profileId);
          toast({ title: 'Supprimé des favoris', description: 'Profil supprimé de vos favoris.' });
        } else {
          await addFavorite(user.id, profileId);
          toast({ title: 'Ajouté aux favoris', description: 'Profil ajouté à vos favoris.' });
        }
      } catch (error) {
        console.error(`Error ${isCurrentlyFavorite ? 'removing' : 'adding'} favorite:`, error);
        toast({
          title: 'Erreur',
          description: `Impossible de ${isCurrentlyFavorite ? 'supprimer des' : 'ajouter aux'} favoris. Veuillez réessayer.`,
          variant: 'destructive',
        });
        // Revert optimistic update on error
        setFavorites(favorites); 
        saveLocalFavorites(favorites); 
      }
    } else {
      // If not logged in, simply show toast based on the action done on localStorage
      if (isCurrentlyFavorite) {
         toast({ title: 'Supprimé des favoris (local)', description: 'Profil supprimé de vos favoris locaux.' });
      } else {
         toast({ title: 'Ajouté aux favoris (local)', description: 'Profil ajouté à vos favoris locaux.' });
      }
       // Redirect to login if trying to add/remove without being logged in? Removed as per original logic (toast/localStorage only)
       // navigate('/login'); 
    }
  }, [favorites, user, toast]); // Removed navigate dependency unless needed for non-logged-in add/remove

  // Helper function to check if an ID is a favorite
  const isFavorite = useCallback((profileId: string): boolean => {
    return favorites.has(profileId);
  }, [favorites]);

  return {
    favorites,
    isLoading,
    toggleFavorite,
    isFavorite,
  };
};
